// component

import {
    Component,
    computed,
    contentChildren,
    inject,
    input,
    OnInit,
    output,
    PLATFORM_ID,
    signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuiFormFieldComponent, AuiFormLayoutComponent } from '@ral/ui:aui/form-layout';

import { isPlatformBrowser } from '@angular/common';
import { AuiCheckboxComponent } from '@ral/ui:aui/checkbox';
import { AuiInputComponent } from '@ral/ui:aui/input';
import { markAllControlsAsDirty } from '@ral/utils:core/forms';
import { injectToastService } from '@ral/utils:core/toast';
import { StoryTitleComponent } from '@storyteller/title';
import { injectInquiryCreateMutation } from '../mutations/inquiry.mutation';
import { AuiItemComponent, AuiLoadingDirective } from '@ral/ui:aui/item';

import { AuiIconComponent } from '@ral/ui:aui/icons';
import { AuiPhoneInputComponent } from '@ral/ui:aui/phone-input';
import { AuiLoadingComponent } from '@ral/ui:aui/aui-loading';
import { CountryCode } from 'libphonenumber-js';
@Component({
    selector: 'app-inquiry-item',
    imports: [AuiItemComponent, AuiIconComponent],
    template: `
        <div class="no-wrap flex items-center justify-between gap-6 pt-3">
            <div class="aui-font-bold-16 flex-grow">
                <span>{{ productName() }}</span>
            </div>
            <!-- <div class="flex-none">
                <span>{{ priceWithoutVat() }}</span>
            </div> -->
            <div class="gatext-center flex items-center">
                @if (numberOfItems() > 1 || (numberOfItems() >= 1 && allowZeroItems())) {
                    <button auiItem [auiStyle]="'text'" [size]="'24'" (click)="decreaseNumberOfItems()">
                        <aui-icon auiItemIcon icon="minus" />
                    </button>
                }
                <span class="min-w-4 px-2 text-center">{{ numberOfItems() }}</span>
                <button auiItem [auiStyle]="'text'" [size]="'24'" (click)="increaseNumberOfItems()">
                    <aui-icon auiItemIcon icon="plus" />
                </button>
            </div>
        </div>
    `,
})
export class InquiryItemComponent implements OnInit {
    productName = input<string>('');
    priceWithoutVat = input<number>(0);
    numberOfItems = signal<number>(1);
    allowZeroItems = input<boolean>(false);
    defaultValue = input<number>(1);

    ngOnInit() {
        this.numberOfItems.set(this.defaultValue());
    }

    decreaseNumberOfItems() {
        if (this.numberOfItems() <= 0 || (this.numberOfItems() <= 1 && !this.allowZeroItems())) {
            return;
        }
        this.numberOfItems.set(this.numberOfItems() - 1);
    }

    increaseNumberOfItems() {
        this.numberOfItems.set(this.numberOfItems() + 1);
    }
}

@Component({
    selector: 'app-inquiry-form',
    imports: [
        AuiFormLayoutComponent,
        AuiFormFieldComponent,
        AuiInputComponent,
        AuiItemComponent,
        ReactiveFormsModule,
        StoryTitleComponent,
        AuiCheckboxComponent,
        AuiLoadingDirective,
        AuiIconComponent,
        AuiPhoneInputComponent,
        AuiLoadingComponent,
    ],
    template: `
        <div
            class="fixed bottom-0 right-0 top-0 z-40 h-screen w-screen cursor-pointer bg-surface-background/40"
            (click)="closeInquiry.emit()"
        ></div>
        <div
            class="fixed bottom-0 right-0 top-0 z-50 w-[720px] max-w-full overflow-y-auto bg-info px-6 text-surface-background md:px-10"
            data-testid="detail-form"
        >
            <div class="pt-4"></div>
            <div class="sticky top-0 z-10 flex items-start justify-between bg-info pb-6 pt-4">
                <story-title [headingLevel]="3">Poptávka produktu</story-title>
                <button auiItem [auiStyle]="'text'" [size]="'32'" (click)="closeInquiry.emit()">
                    <aui-icon auiItemIcon icon="xmark" />
                </button>
            </div>
            <ng-content></ng-content>
            @if (totalPrice() !== -1) {
                <span class="aui-font-bold-24 block pb-8 pt-10 text-right">
                    Celkem {{ totalPriceText() }} bez DPH
                </span>
            } @else {
                <span class="aui-font-bold-24 block pb-8 pt-10 text-right"> Pro získání ceny nás kontaktujte </span>
            }

            <p class="aui-font-bold-18 block pb-6 pt-2">Co bude dál?</p>
            <ol>
                <li class="relative pb-4 pl-10">
                    <span
                        class="aui-font-regular-16 absolute left-0 top-0 mr-8 inline-block h-6 w-6 rounded-full bg-surface-background p-0.5 text-center text-info"
                    >
                        1
                    </span>
                    <span>Odešlete nezávaznou poptávku</span>
                </li>
                <li class="relative pb-4 pl-10">
                    <span
                        class="aui-font-regular-16 absolute left-0 top-0 mr-8 inline-block h-6 w-6 rounded-full bg-surface-background p-0.5 text-center text-info"
                    >
                        2</span
                    >
                    <span class="story-gray-text">Zavoláme vám a&nbsp;naplánujeme schůzku, kde upřesníme techincké detaily</span>
                </li>
                <li class="relative pb-4 pl-10">
                    <span
                        class="aui-font-regular-16 absolute left-0 top-0 mr-8 inline-block h-6 w-6 rounded-full bg-surface-background p-0.5 text-center text-info"
                    >
                        3
                    </span>
                    <span class="story-gray-text">Odešleme Vám nabídku</span>
                </li>
                <li class="relative pb-4 pl-10">
                    <span
                        class="aui-font-regular-16 absolute left-0 top-0 mr-8 inline-block h-6 w-6 rounded-full bg-surface-background p-0.5 text-center text-info"
                    >
                        4</span
                    >
                    <span class="story-gray-text"
                        >Po podepsání nabídky připravíme produkt přesně pro Vás a&nbsp;odešleme</span
                    >
                </li>
                <li class="relative pb-4 pl-10">
                    <span
                        class="aui-font-regular-16 absolute left-0 top-0 mr-8 inline-block h-6 w-6 rounded-full bg-surface-background p-0.5 text-center text-info"
                    >
                        5</span
                    >
                    <span class="story-gray-text"
                        >Produkt nainstalujeme, uvedeme do provozu a&nbsp;zaškolíme vás a&nbsp;vaše zaměstnance</span
                    >
                </li>
                <li class="relative pb-4 pl-10">
                    <span
                        class="aui-font-regular-16 absolute left-0 top-0 mr-8 inline-block h-6 w-6 rounded-full bg-surface-background p-0.5 text-center text-info"
                    >
                        6</span
                    >
                    <span class="story-gray-text"
                        >Užíváte si automatizovanou výrobu</span
                    >
                </li>
            </ol>
            <p class="story-gray-text">
                Děláme vše pro to, abychom Vám zaručili plnou kompatibilitu našich produktů pro Vaše potřeby.
            </p>

            <div aria-live="polite" aria-atomic="true">
                @defer (on viewport) {
                    <span class="aui-font-bold-18 block pb-8 pt-12">Kontaktní údaje</span>
                    <form [formGroup]="form" (ngSubmit)="submit()" data-testid="form">
                        <aui-form-layout>
                            <aui-form-field>
                                <aui-input
                                    [size]="'48'"
                                    placeholder="Jméno společnosti"
                                    [formControl]="form.controls.companyName"
                                    data-testid="companyName"
                                    autoCompleteProp="organization"
                                />
                            </aui-form-field>
                            <aui-form-field>
                                <aui-input
                                    [size]="'48'"
                                    placeholder="IČO"
                                    [formControl]="form.controls.ico"
                                    data-testid="companyIco"
                                />
                            </aui-form-field>
                            <aui-form-field>
                                <aui-input
                                    [size]="'48'"
                                    placeholder="Jméno"
                                    [formControl]="form.controls.personName"
                                    data-testid="personName"
                                    autoCompleteProp="given-name"
                                />
                            </aui-form-field>
                            <aui-form-field>
                                <aui-input
                                    [size]="'48'"
                                    placeholder="Příjmení"
                                    [formControl]="form.controls.personSurname"
                                    data-testid="personSurname"
                                    autoCompleteProp="family-name"
                                />
                            </aui-form-field>
                            <aui-form-field>
                                <aui-input
                                    [size]="'48'"
                                    placeholder="Emailová adresa"
                                    [formControl]="form.controls.email"
                                    data-testid="email"
                                    autoCompleteProp="email"
                                />
                            </aui-form-field>
                            <aui-form-field>
                                <aui-phone-input
                                    [size]="'48'"
                                    placeholder="Telefonní číslo"
                                    [formControl]="form.controls.phone"
                                    data-testid="phone"
                                    [defaultCountry]="defaultCountry()"
                                />
                            </aui-form-field>
                            <aui-form-field>
                                <aui-checkbox
                                    [formControl]="form.controls.confirmation"
                                    [checkboxLabel]="'Souhlasím se '"
                                    data-testid="confirmation"
                                >
                                    <a href="/assets/privacy-policy.pdf" target="_blank" class="link">
                                        zpracováním osobních údajů</a
                                    >
                                </aui-checkbox>
                            </aui-form-field>
                            <div class="sticky bottom-0 z-10 bg-info py-3">
                                <button
                                    auiItem
                                    [auiLoading]="mutation.isPending()"
                                    [disabled]="mutation.isPending()"
                                    data-testid="submitButton"
                                    [size]="'40'"
                                    [color]="'primary'"
                                    [fullWidth]="true"
                                >
                                    <span auiItemLabel>Dokončit poptávku</span>
                                </button>
                            </div>
                        </aui-form-layout>
                    </form>
                } @placeholder {
                    <div class="p-2 pt-10 text-center">
                        <aui-loading />
                        <p class="story-gray-text mt-4">Načítání...</p>
                    </div>
                }
                <div class="pb-4"></div>
            </div>
        </div>
    `,
})
export class InquiryComponent implements OnInit {
    mutation = injectInquiryCreateMutation();
    closeInquiry = output<void>();
    productName = input<string>('');
    platform = inject(PLATFORM_ID);
    toastService = injectToastService();
    defaultCountry = input<CountryCode | null>('CZ');
    form = new FormGroup({
        companyName: new FormControl(''),
        ico: new FormControl(''),
        personName: new FormControl('', [Validators.required]),
        personSurname: new FormControl('', [Validators.required]),
        email: new FormControl('', [Validators.required, Validators.email]),
        // Allow only numbers "-", " ", "+", "(", ")"
        phone: new FormControl('', [Validators.required, Validators.pattern(/^[0-9\-\\+\\(\\)\s]*$/)]),
        confirmation: new FormControl(false, [Validators.requiredTrue]),
    });

    items = contentChildren<InquiryItemComponent>(InquiryItemComponent);

    totalPrice = computed(() => {
        if (this.items().some((item) => item.numberOfItems() > 0 && item.priceWithoutVat() === 0)) {
            return -1;
        }
        return this.items()
            .map((item) => item.priceWithoutVat() * item.numberOfItems())
            .reduce((a, b) => a + b, 0);
    });

    totalCount = computed(() => {
        return this.items()
            .map((item) => item.numberOfItems())
            .reduce((a, b) => a + b, 0);
    });

    totalPriceText = computed(() => {
        return Intl.NumberFormat('cs-CZ', {
            style: 'currency',
            currency: 'CZK',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(this.totalPrice());
    });

    ngOnInit() {
        if (isPlatformBrowser(this.platform)) {
            (window as any).dataLayer = (window as any).dataLayer || [];
            (window as any).dataLayer.push({
                event: 'inquiryFormOpened',
                productName: this.productName(),
            } as any);
        }
    }

    async submit() {
        const items = this.items();

        const note = items
            .map((item) => item.numberOfItems() + 'x ' + item.productName() + ' ' + item.priceWithoutVat())
            .join(', ');

        this.form.markAllAsTouched();
        markAllControlsAsDirty(this.form);
        this.form.updateValueAndValidity({ emitEvent: false });

        if (this.form.invalid) {
            return;
        }

        if (isPlatformBrowser(this.platform)) {
            (window as any).dataLayer = (window as any).dataLayer || [];
            (window as any).dataLayer.push({
                event: 'inquiry_submit',
                companyName: this.form.value.companyName!,
                ico: this.form.value.ico!,
                personName: this.form.value.personName!,
                personSurname: this.form.value.personSurname!,
                email: this.form.value.email!,
                phone: this.form.value.phone!,
                productName: this.productName(),
                note,
                priceAtInquiry: this.totalPrice(),
                count: this.totalCount(),
                consentId:
                    document.cookie
                        .split('; ')
                        .find((row) => row.startsWith('cookieyes-consent='))
                        ?.split('=')[1] || '',
                gclId:
                    document.cookie
                        .split('; ')
                        .find((row) => row.startsWith('gclid='))
                        ?.split('=')[1] || '',
            } as any);
        }

        await this.mutation.mutateAsync(
            {
                companyName: this.form.value.companyName!,
                ico: this.form.value.ico!,
                personName: this.form.value.personName!,
                personSurname: this.form.value.personSurname!,
                email: this.form.value.email!,
                phone: this.form.value.phone!,
                productName: this.productName(),
                note,
                priceAtInquiry: this.totalPrice(),
                // get consentId and gclId from cookies
                consentId:
                    document.cookie
                        .split('; ')
                        .find((row) => row.startsWith('cookieyes-consent='))
                        ?.split('=')[1] || '',
                gclId:
                    document.cookie
                        .split('; ')
                        .find((row) => row.startsWith('gclid='))
                        ?.split('=')[1] || '',
                count: this.totalCount(),
            },
            {
                onSuccess: () => {
                    this.form.reset();
                },
            }
        );
    }

    closeInquiryForm() {
        this.closeInquiry.emit();
    }
}
